/**
 * State module - application state management
 */

export class AppState {
  constructor() {
    this.isRaceStarted = false;
    this.startTime = null;
    this.finishTime = null;
    this.currentNumber = '';
    this.results = [];
    this.listeners = new Map();
  }

  /**
   * Subscribe to state changes
   * @param {string} key - State key to watch
   * @param {Function} callback - Callback function
   */
  subscribe(key, callback) {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, []);
    }
    this.listeners.get(key).push(callback);
  }

  /**
   * Notify listeners of state change
   * @param {string} key - State key that changed
   * @param {*} value - New value
   */
  notify(key, value) {
    if (this.listeners.has(key)) {
      this.listeners.get(key).forEach(callback => callback(value));
    }
  }

  /**
   * Start race
   * @param {number} timestamp - Start timestamp
   */
  startRace(timestamp) {
    this.isRaceStarted = true;
    this.startTime = timestamp;
    this.finishTime = null;
    this.currentNumber = '';
    this.results = [];
    this.notify('raceStarted', { startTime: timestamp });
  }

  /**
   * Finish race
   * @param {number} timestamp - Finish timestamp
   */
  finishRace(timestamp) {
    this.isRaceStarted = false;
    this.finishTime = timestamp;
    this.currentNumber = '';
    this.notify('raceFinished', { finishTime: timestamp });
  }

  /**
   * Update current number being entered
   * @param {string} number - Number string
   */
  setCurrentNumber(number) {
    this.currentNumber = number;
    this.notify('currentNumberChanged', number);
  }

  /**
   * Add result
   * @param {object} result - Result object {number, time, timestamp}
   */
  addResult(result) {
    this.results.unshift(result); // Add to beginning for newest first
    this.notify('resultAdded', result);
    this.notify('resultsChanged', this.results);
  }

  /**
   * Load state from storage
   * @param {object} data - Data from storage
   */
  loadFromStorage(data) {
    this.startTime = data.startTime;
    this.finishTime = data.finishTime;
    this.results = data.results || [];
    this.isRaceStarted = data.startTime !== null && data.finishTime === null;

    if (this.isRaceStarted) {
      this.notify('raceStarted', { startTime: this.startTime, restored: true });
    }
    if (this.results.length > 0) {
      this.notify('resultsChanged', this.results);
    }
  }

  /**
   * Clear all state
   */
  clear() {
    this.isRaceStarted = false;
    this.startTime = null;
    this.finishTime = null;
    this.currentNumber = '';
    this.results = [];
    this.notify('stateCleared', null);
  }
}
